import { QuantityField } from "@/components/ClustersPage/QuantityField/QuantityField";
import { OptionType } from "@/components/forms/inputs/SelectInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ClusterType, SimpleClusterForm } from "@/types";
import { getDurationQuantity, getMinDuration } from "@/utils/cluster";
import { isPaygCluster } from "@/utils/payg";
import { cn } from "@/utils/styles";
import { useFormikContext } from "formik";
import { SavedInConfigMarker } from "@/components/ClustersPage/SavedDeploymentConfigs/SavedInConfigMarker";

type QuantityDurationSectionProps = {
  durationOptions: { value: string; label: string }[];
  availableQuantity?: OptionType[];
  quantityFieldName?: string;
  quantityLabel?: string;
  quantitySubtitle?: string;
  /** When false, hides the quantity/replica stepper row. */
  showQuantityStepper?: boolean;
  className?: string;
};

export const QuantityDurationSection: React.FC<QuantityDurationSectionProps> = ({
  durationOptions,
  availableQuantity = [],
  quantityFieldName = "replicaCount",
  quantityLabel = "Replicas",
  quantitySubtitle = "How many replicas of the container to deploy",
  showQuantityStepper = true,
  className
}) => {
  const { values, setFieldValue } = useFormikContext<SimpleClusterForm>();

  const minDuration = getMinDuration(values.clusterType as ClusterType, values.durationType);
  const isPayg = isPaygCluster(values.clusterType);
  const showReplicaStepper = showQuantityStepper && availableQuantity.length > 0;
  const quantityBounds = availableQuantity
    .map(({ value }) => parseInt(value, 10))
    .filter((n) => !Number.isNaN(n));
  const minQuantity = quantityBounds.length > 0 ? Math.min(...quantityBounds) : 1;
  const maxQuantity = quantityBounds.length > 0 ? Math.max(...quantityBounds) : minQuantity;
  const quantityValue = values[quantityFieldName as keyof SimpleClusterForm];

  if (isPayg && !showReplicaStepper) {
    return null;
  }

  return (
    <div className={cn("flex flex-col", className)}>
      {showReplicaStepper && (
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-2 text-[16px] font-medium leading-[24px] text-foreground">
              {quantityLabel}
              <SavedInConfigMarker />
            </span>
            <span className="text-[14px] leading-[20px] text-muted-foreground">
              {quantitySubtitle}
            </span>
          </div>
          <QuantityField
            name={quantityFieldName}
            min={minQuantity}
            max={maxQuantity}
            placeholder={String(quantityValue ?? minQuantity)}
            inputClassName="w-[56px] min-w-[56px] shrink-0 flex-none px-2"
            className="shrink-0 items-center"
          />
        </div>
      )}

      {showReplicaStepper && !isPayg && <Separator className="my-4" />}

      {!isPayg && (
        <div
          className={cn("flex items-center justify-between", !showReplicaStepper && "pt-[18px]")}
        >
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-2 text-[16px] font-medium leading-[24px] text-foreground">
              Duration
              <SavedInConfigMarker />
            </span>
            <span className="text-[14px] leading-[20px] text-muted-foreground">
              Select how long you want to rent the GPUs
            </span>
          </div>
          <div className="flex items-center gap-2">
            <QuantityField
              name="durationQuantity"
              min={minDuration}
              placeholder={String(values.durationQuantity ?? minDuration)}
              inputClassName="w-[56px] min-w-[56px] shrink-0 flex-none px-2"
              className="shrink-0 items-center"
            />
            <Select
              value={values.durationType}
              onValueChange={(value) => {
                const newQty = getDurationQuantity(
                  value,
                  values.durationType,
                  values.durationQuantity
                );
                setFieldValue("durationType", value);
                if (typeof newQty === "number" && newQty >= 1) {
                  setFieldValue("durationQuantity", newQty);
                }
              }}
            >
              <SelectTrigger className="h-[36px] min-w-[140px]">
                <SelectValue placeholder="Duration" />
              </SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)]">
                {durationOptions.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};
